import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';
import AdminTabs from '../components/AdminTabs';
import Toast from '../components/Toast';
import { API_BASE_URL } from '../config/api';

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Helper function to make authenticated requests
const authAxios = {
  get: (url, config = {}) => axios.get(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  post: (url, data, config = {}) => axios.post(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  put: (url, data, config = {}) => axios.put(url, data, { ...config, headers: { ...config.headers, ...getAuthHeaders() } }),
  delete: (url, config = {}) => axios.delete(url, { ...config, headers: { ...config.headers, ...getAuthHeaders() } })
};

function ExternalImagesPage() {
  const [activeTab, setActiveTab] = useState('images'); // 'images' | 'links' | 'search-replace'

  // Search/Replace Notes (simulation-first: find only unless user applies replace)
  const [replaceSearchText, setReplaceSearchText] = useState('');
  const [replaceReplaceText, setReplaceReplaceText] = useState('');
  const [replaceFindResult, setReplaceFindResult] = useState(null); // { matches: { noteName, title, count }[], totalCount }
  const [replaceInProgress, setReplaceInProgress] = useState(false);
  const [replaceApplyResult, setReplaceApplyResult] = useState(null); // { updated: number, errors: string[] }
  
  // Images state
  const [externalImages, setExternalImages] = useState([]);
  const [externalImagesLoading, setExternalImagesLoading] = useState(false);
  const [selectedImages, setSelectedImages] = useState(new Set());
  const [downloadingImages, setDownloadingImages] = useState(false);
  const [downloadResult, setDownloadResult] = useState(null);
  const [downloadError, setDownloadError] = useState(null);
  const [sortColumn, setSortColumn] = useState(null); // 'url' or 'note'
  const [sortDirection, setSortDirection] = useState('asc'); // 'asc' or 'desc'
  const [imagesSearchTerm, setImagesSearchTerm] = useState('');
  
  // Links state
  const [externalLinks, setExternalLinks] = useState([]);
  const [externalLinksLoading, setExternalLinksLoading] = useState(false);
  const [linkEdits, setLinkEdits] = useState(new Map()); // Map of url -> newUrl
  const [updatingLinks, setUpdatingLinks] = useState(false);
  const [updateResult, setUpdateResult] = useState(null);
  const [updateError, setUpdateError] = useState(null);
  const [linksSortColumn, setLinksSortColumn] = useState(null); // 'url' or 'note'
  const [linksSortDirection, setLinksSortDirection] = useState('asc');
  const [linksSearchTerm, setLinksSearchTerm] = useState('');
  const [bulkReplaceSearch, setBulkReplaceSearch] = useState('');
  const [bulkReplaceWith, setBulkReplaceWith] = useState('');
  
  const [toast, setToast] = useState(null);

  const fetchExternalImages = async () => {
    setExternalImagesLoading(true);
    setExternalImages([]);
    setSelectedImages(new Set());
    setDownloadResult(null);
    setDownloadError(null);
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/external-images`);
      if (response.data.success) {
        setExternalImages(response.data.data.images || []);
        if (response.data.data.images && response.data.data.images.length === 0) {
          setToast({ message: 'No external images found in any notes.', type: 'info' });
        } else {
          setToast({ message: `Found ${response.data.data.images.length} external image(s).`, type: 'success' });
        }
      } else {
        setDownloadError(response.data.error || 'Failed to scan external images');
        setToast({ message: response.data.error || 'Failed to scan external images', type: 'error' });
      }
    } catch (err) {
      console.error('Error scanning external images:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to scan external images';
      setDownloadError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setExternalImagesLoading(false);
    }
  };

  const handleDownloadImages = async () => {
    if (selectedImages.size === 0) {
      setDownloadError('Please select at least one image to download');
      setToast({ message: 'Please select at least one image to download', type: 'error' });
      return;
    }
    setDownloadingImages(true);
    setDownloadResult(null);
    setDownloadError(null);
    try {
      const imageUrls = Array.from(selectedImages);
      const response = await authAxios.post(
        `${API_BASE_URL}/admin/utilities/download-external-images`,
        { imageUrls }
      );
      if (response.data.success) {
        setDownloadResult(response.data.data);
        // Refresh the image list to remove downloaded images
        const remainingImages = externalImages.filter(img => !selectedImages.has(img.url));
        setExternalImages(remainingImages);
        setSelectedImages(new Set());
        const successCount = response.data.data.downloaded?.length || 0;
        const failCount = response.data.data.failed?.length || 0;
        if (failCount > 0) {
          setToast({ message: `Downloaded ${successCount} image(s), ${failCount} failed.`, type: 'warning' });
        } else {
          setToast({ message: `Successfully downloaded ${successCount} image(s) and updated HTML files.`, type: 'success' });
        }
      } else {
        const errorMsg = response.data.error || 'Failed to download images';
        setDownloadError(errorMsg);
        setToast({ message: errorMsg, type: 'error' });
      }
    } catch (err) {
      console.error('Error downloading images:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to download images';
      setDownloadError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setDownloadingImages(false);
    }
  };

  // Sort and filter images based on current sort settings and search term
  const sortedImages = useMemo(() => {
    let filtered = externalImages;
    
    // Apply search filter
    if (imagesSearchTerm.trim()) {
      const searchLower = imagesSearchTerm.toLowerCase().trim();
      filtered = externalImages.filter(image => 
        image.url.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (!sortColumn) return filtered;
    
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (sortColumn === 'url') {
        comparison = a.url.localeCompare(b.url);
      } else if (sortColumn === 'note') {
        const aNote = (a.noteTitle || a.noteName || '').toLowerCase();
        const bNote = (b.noteTitle || b.noteName || '').toLowerCase();
        comparison = aNote.localeCompare(bNote);
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [externalImages, sortColumn, sortDirection, imagesSearchTerm]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort and filter links based on current sort settings and search term
  const sortedLinks = useMemo(() => {
    let filtered = externalLinks;
    
    // Apply search filter
    if (linksSearchTerm.trim()) {
      const searchLower = linksSearchTerm.toLowerCase().trim();
      filtered = externalLinks.filter(link => 
        link.url.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply sorting
    if (!linksSortColumn) return filtered;
    
    const sorted = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      if (linksSortColumn === 'url') {
        comparison = a.url.localeCompare(b.url);
      } else if (linksSortColumn === 'note') {
        const aNote = (a.noteTitle || a.noteName || '').toLowerCase();
        const bNote = (b.noteTitle || b.noteName || '').toLowerCase();
        comparison = aNote.localeCompare(bNote);
      }
      
      return linksSortDirection === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [externalLinks, linksSortColumn, linksSortDirection, linksSearchTerm]);

  const handleLinksSort = (column) => {
    if (linksSortColumn === column) {
      setLinksSortDirection(linksSortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setLinksSortColumn(column);
      setLinksSortDirection('asc');
    }
  };

  const fetchExternalLinks = async () => {
    setExternalLinksLoading(true);
    setExternalLinks([]);
    setLinkEdits(new Map());
    setUpdateResult(null);
    setUpdateError(null);
    try {
      const response = await authAxios.get(`${API_BASE_URL}/admin/utilities/external-links`);
      if (response.data.success) {
        setExternalLinks(response.data.data.links || []);
        if (response.data.data.links && response.data.data.links.length === 0) {
          setToast({ message: 'No external links found in any notes.', type: 'info' });
        } else {
          setToast({ message: `Found ${response.data.data.links.length} external link(s).`, type: 'success' });
        }
      } else {
        setUpdateError(response.data.error || 'Failed to scan external links');
        setToast({ message: response.data.error || 'Failed to scan external links', type: 'error' });
      }
    } catch (err) {
      console.error('Error scanning external links:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to scan external links';
      setUpdateError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setExternalLinksLoading(false);
    }
  };

  const handleBulkReplace = () => {
    if (!bulkReplaceSearch.trim()) {
      setToast({ message: 'Please enter text to search for', type: 'error' });
      return;
    }

    const newEdits = new Map(linkEdits);
    let replaceCount = 0;

    sortedLinks.forEach((link) => {
      const currentUrl = newEdits.get(link.url) ?? link.url;
      if (currentUrl.includes(bulkReplaceSearch)) {
        // Escape special regex characters for literal matching
        const escapedSearch = bulkReplaceSearch.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const newUrl = currentUrl.replace(new RegExp(escapedSearch, 'g'), bulkReplaceWith);
        newEdits.set(link.url, newUrl);
        replaceCount++;
      }
    });

    setLinkEdits(newEdits);
    setToast({ 
      message: `Bulk replace applied to ${replaceCount} link(s). Review changes and click "Save Changes" to apply.`, 
      type: 'success' 
    });
    
    // Clear the bulk replace fields
    setBulkReplaceSearch('');
    setBulkReplaceWith('');
  };

  const handleUpdateLinks = async () => {
    if (linkEdits.size === 0) {
      setUpdateError('No changes to save');
      setToast({ message: 'No changes to save', type: 'error' });
      return;
    }
    
    setUpdatingLinks(true);
    setUpdateResult(null);
    setUpdateError(null);
    try {
      const updates = Array.from(linkEdits.entries()).map(([url, newUrl]) => ({
        url,
        newUrl
      }));
      
      const response = await authAxios.put(
        `${API_BASE_URL}/admin/utilities/external-links/update`,
        { updates }
      );
      
      if (response.data.success) {
        setUpdateResult(response.data.data);
        // Refresh the links list to show updated URLs
        await fetchExternalLinks();
        const successCount = response.data.data.updated?.length || 0;
        const failCount = response.data.data.failed?.length || 0;
        if (failCount > 0) {
          setToast({ message: `Updated ${successCount} link(s), ${failCount} failed.`, type: 'warning' });
        } else {
          setToast({ message: `Successfully updated ${successCount} link(s).`, type: 'success' });
        }
      } else {
        const errorMsg = response.data.error || 'Failed to update links';
        setUpdateError(errorMsg);
        setToast({ message: errorMsg, type: 'error' });
      }
    } catch (err) {
      console.error('Error updating links:', err);
      const errorMsg = err.response?.data?.error || err.message || 'Failed to update links';
      setUpdateError(errorMsg);
      setToast({ message: errorMsg, type: 'error' });
    } finally {
      setUpdatingLinks(false);
    }
  };

  // Search/Replace Notes: simulation (find only) - no writes
  const handleReplaceFind = async () => {
    const search = (replaceSearchText || '').trim();
    if (!search) {
      setToast({ message: 'Enter text to search for.', type: 'error' });
      return;
    }
    setReplaceFindResult(null);
    setReplaceApplyResult(null);
    setReplaceInProgress(true);
    try {
      const listRes = await authAxios.get(`${API_BASE_URL}/admin/notes`);
      if (!listRes.data.success || !Array.isArray(listRes.data.data)) {
        setToast({ message: 'Failed to load notes list.', type: 'error' });
        return;
      }
      const noteList = listRes.data.data;
      const matches = [];
      let totalCount = 0;
      for (const note of noteList) {
        try {
          const getRes = await authAxios.get(`${API_BASE_URL}/admin/notes/${encodeURIComponent(note.name)}`);
          if (!getRes.data.success || getRes.data.data == null) continue;
          const content = getRes.data.data.content;
          if (typeof content !== 'string') continue;
          const count = search ? (content.split(search).length - 1) : 0;
          if (count > 0) {
            matches.push({ noteName: note.name, title: note.title || note.name, count });
            totalCount += count;
          }
        } catch {
          // skip failed note
        }
      }
      setReplaceFindResult({ matches, totalCount });
      if (matches.length === 0) {
        setToast({ message: 'No matches found in any note.', type: 'info' });
      } else {
        setToast({ message: `Found ${totalCount} match(es) in ${matches.length} note(s).`, type: 'success' });
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }
      setToast({ message: err.response?.data?.error || err.message || 'Find failed.', type: 'error' });
    } finally {
      setReplaceInProgress(false);
    }
  };

  // Search/Replace Notes: apply replace (writes) - only after user explicitly runs it
  const handleReplaceApply = async () => {
    const search = (replaceSearchText || '').trim();
    if (!search) {
      setToast({ message: 'Enter text to search for.', type: 'error' });
      return;
    }
    const replaceWith = replaceReplaceText ?? '';
    setReplaceApplyResult(null);
    setReplaceInProgress(true);
    const errors = [];
    let updated = 0;
    try {
      const listRes = await authAxios.get(`${API_BASE_URL}/admin/notes`);
      if (!listRes.data.success || !Array.isArray(listRes.data.data)) {
        setReplaceApplyResult({ updated: 0, errors: ['Failed to load notes list.'] });
        setToast({ message: 'Failed to load notes list.', type: 'error' });
        return;
      }
      const noteList = listRes.data.data;
      for (const note of noteList) {
        try {
          const getRes = await authAxios.get(`${API_BASE_URL}/admin/notes/${encodeURIComponent(note.name)}`);
          if (!getRes.data.success || getRes.data.data == null) {
            errors.push(`${note.title || note.name}: failed to load`);
            continue;
          }
          const content = getRes.data.data.content;
          if (typeof content !== 'string' || !content.includes(search)) continue;
          const newContent = content.split(search).join(replaceWith);
          await authAxios.put(`${API_BASE_URL}/admin/notes/${encodeURIComponent(note.name)}`, { content: newContent });
          updated += 1;
        } catch (err) {
          const msg = err.response?.data?.error || err.message || 'Unknown error';
          errors.push(`${note.title || note.name}: ${msg}`);
        }
      }
      setReplaceApplyResult({ updated, errors });
      if (updated > 0) {
        setToast({ message: `Replaced in ${updated} note(s).`, type: 'success' });
        setReplaceFindResult(null);
      }
      if (errors.length > 0 && updated === 0) {
        setToast({ message: 'Replace failed for all notes.', type: 'error' });
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem('authToken');
        sessionStorage.removeItem('authToken');
        window.location.href = '/login';
        return;
      }
      setReplaceApplyResult({ updated: 0, errors: [err.response?.data?.error || err.message || 'Request failed'] });
      setToast({ message: 'Replace in all notes failed.', type: 'error' });
    } finally {
      setReplaceInProgress(false);
    }
  };

  return (
    <div className="min-h-screen py-8 px-4 bg-gray-50">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
      <div className="max-w-7xl mx-auto">
        <AdminHeader />

        <AdminTabs />

        <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">External Resources Manager</h2>
          
          {/* Tabs */}
          <div className="flex gap-2 border-b border-gray-200 mb-6">
            <button
              onClick={() => setActiveTab('images')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'images'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              External Images
            </button>
            <button
              onClick={() => setActiveTab('links')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'links'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              External Links
            </button>
            <button
              onClick={() => setActiveTab('search-replace')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'search-replace'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              Search/Replace Notes
            </button>
          </div>

          {/* Images Tab */}
          {activeTab === 'images' && (
            <>
              <p className="text-sm text-gray-600 mb-6">
                Scan all notes for external images (http:// or https:// URLs), download them to the static content directory, and automatically update HTML references to use local paths.
              </p>

          {downloadError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-800">{downloadError}</p>
            </div>
          )}

          {downloadResult && (
            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
              <div className="mb-2">
                <p className="text-sm font-medium text-blue-900">Download Results</p>
              </div>
              <div className="text-sm space-y-1">
                <p className="text-green-700">
                  <strong>Downloaded:</strong> {downloadResult.downloaded?.length || 0} image(s)
                </p>
                {downloadResult.failed?.length > 0 && (
                  <p className="text-red-700">
                    <strong>Failed:</strong> {downloadResult.failed.length} image(s)
                  </p>
                )}
                <p className="text-blue-700">
                  <strong>Pages Updated:</strong> {downloadResult.updated?.length || 0}
                </p>
                {downloadResult.downloaded?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-blue-900 mb-1">Downloaded Images:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {downloadResult.downloaded.map((item, idx) => (
                        <p key={idx} className="text-xs text-blue-700">
                          • {item.filename} → {item.path}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
                {downloadResult.failed?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-medium text-red-900 mb-1">Failed Downloads:</p>
                    <div className="max-h-40 overflow-y-auto space-y-1">
                      {downloadResult.failed.map((item, idx) => (
                        <p key={idx} className="text-xs text-red-700">
                          • {item.url}: {item.error}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="mb-4 flex items-center gap-4">
            <button
              onClick={fetchExternalImages}
              disabled={externalImagesLoading}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {externalImagesLoading ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Scanning...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Scan for External Images
                </>
              )}
            </button>
            
            {externalImages.length > 0 && (
              <div className="flex-1 max-w-md">
                <div className="relative">
                  <input
                    type="text"
                    value={imagesSearchTerm}
                    onChange={(e) => setImagesSearchTerm(e.target.value)}
                    placeholder="Search by URL..."
                    className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <svg 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  {imagesSearchTerm && (
                    <button
                      onClick={() => setImagesSearchTerm('')}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {!externalImagesLoading && externalImages.length === 0 && !downloadError && (
            <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
              <div className="flex items-center gap-2">
                <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-green-900">
                  No external images found in any notes. All images are already using local paths.
                </p>
              </div>
            </div>
          )}

          {externalImages.length > 0 && (
            <div className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-gray-700">
                  {imagesSearchTerm.trim() ? (
                    <>
                      Showing {sortedImages.length} of {externalImages.length} external image(s)
                      {sortColumn && (
                        <span className="text-gray-500 ml-2">
                          (sorted by {sortColumn === 'url' ? 'Image URL' : 'Note'} {sortDirection === 'asc' ? '↑' : '↓'})
                        </span>
                      )}
                    </>
                  ) : (
                    <>
                      Found {externalImages.length} external image(s)
                      {sortColumn && (
                        <span className="text-gray-500 ml-2">
                          (sorted by {sortColumn === 'url' ? 'Image URL' : 'Note'} {sortDirection === 'asc' ? '↑' : '↓'})
                        </span>
                      )}
                    </>
                  )}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const allUrls = new Set(sortedImages.map(img => img.url));
                      setSelectedImages(allUrls);
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Select All
                  </button>
                  <span className="text-gray-400">|</span>
                  <button
                    onClick={() => setSelectedImages(new Set())}
                    className="text-xs text-blue-600 hover:text-blue-800"
                  >
                    Deselect All
                  </button>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={selectedImages.size === sortedImages.length && sortedImages.length > 0}
                          onChange={(e) => {
                            if (e.target.checked) {
                              const allUrls = new Set(sortedImages.map(img => img.url));
                              setSelectedImages(allUrls);
                            } else {
                              setSelectedImages(new Set());
                            }
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('url')}
                      >
                        <div className="flex items-center gap-1">
                          Image URL
                          {sortColumn === 'url' && (
                            <span className="text-gray-700">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th 
                        className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                        onClick={() => handleSort('note')}
                      >
                        <div className="flex items-center gap-1">
                          Note
                          {sortColumn === 'note' && (
                            <span className="text-gray-700">
                              {sortDirection === 'asc' ? '↑' : '↓'}
                            </span>
                          )}
                        </div>
                      </th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Occurrences
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sortedImages.map((image, idx) => (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-3 py-2 whitespace-nowrap">
                          <input
                            type="checkbox"
                            checked={selectedImages.has(image.url)}
                            onChange={(e) => {
                              const newSelected = new Set(selectedImages);
                              if (e.target.checked) {
                                newSelected.add(image.url);
                              } else {
                                newSelected.delete(image.url);
                              }
                              setSelectedImages(newSelected);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </td>
                        <td className="px-3 py-2">
                          <div className="text-xs text-gray-900 max-w-md truncate" title={image.url}>
                            {image.url}
                          </div>
                          {image.context && (
                            <div className="text-xs text-gray-500 mt-1 max-w-md truncate" title={image.context}>
                              {image.context}
                            </div>
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <Link
                            to={`/note/${image.noteSlug}`}
                            className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {image.noteTitle || image.noteName}
                          </Link>
                          <div className="text-xs text-gray-500">{image.noteSlug}</div>
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                          {image.occurrences}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {externalImages.length > 0 && (
            <button
              onClick={handleDownloadImages}
              disabled={downloadingImages || selectedImages.size === 0}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {downloadingImages ? (
                <>
                  <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Downloading...
                </>
              ) : (
                <>
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download Selected Images ({selectedImages.size})
                </>
              )}
            </button>
          )}
            </>
          )}

          {/* Links Tab */}
          {activeTab === 'links' && (
            <>
              <p className="text-sm text-gray-600 mb-6">
                Scan all notes for external links (http:// or https:// URLs in &lt;a href&gt; tags) and manually update their URLs.
              </p>

              {updateError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-800">{updateError}</p>
                </div>
              )}

              {updateResult && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="mb-2">
                    <p className="text-sm font-medium text-blue-900">Update Results</p>
                  </div>
                  <div className="text-sm space-y-1">
                    <p className="text-green-700">
                      <strong>Updated:</strong> {updateResult.updated?.length || 0} link(s)
                    </p>
                    {updateResult.failed?.length > 0 && (
                      <p className="text-red-700">
                        <strong>Failed:</strong> {updateResult.failed.length} link(s)
                      </p>
                    )}
                    {updateResult.updated?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-blue-900 mb-1">Updated Links:</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {updateResult.updated.map((item, idx) => (
                            <p key={idx} className="text-xs text-blue-700">
                              • {item.url} → {item.newUrl || '(removed)'} ({item.pages?.length || 0} page(s))
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                    {updateResult.failed?.length > 0 && (
                      <div className="mt-3">
                        <p className="text-xs font-medium text-red-900 mb-1">Failed Updates:</p>
                        <div className="max-h-40 overflow-y-auto space-y-1">
                          {updateResult.failed.map((item, idx) => (
                            <p key={idx} className="text-xs text-red-700">
                              • {item.url}: {item.error}
                            </p>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="mb-4 flex items-center gap-4">
                <button
                  onClick={fetchExternalLinks}
                  disabled={externalLinksLoading}
                  className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                >
                  {externalLinksLoading ? (
                    <>
                      <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Scanning...
                    </>
                  ) : (
                    <>
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Scan for External Links
                    </>
                  )}
                </button>
                
                {externalLinks.length > 0 && (
                  <div className="flex-1 max-w-md">
                    <div className="relative">
                      <input
                        type="text"
                        value={linksSearchTerm}
                        onChange={(e) => setLinksSearchTerm(e.target.value)}
                        placeholder="Search by URL..."
                        className="w-full px-4 py-2 pl-10 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                      <svg 
                        className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      {linksSearchTerm && (
                        <button
                          onClick={() => setLinksSearchTerm('')}
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {!externalLinksLoading && externalLinks.length === 0 && !updateError && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md">
                  <div className="flex items-center gap-2">
                    <svg className="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <p className="text-sm font-medium text-green-900">
                      No external links found in any notes. All links are already using local paths.
                    </p>
                  </div>
                </div>
              )}

              {externalLinks.length > 0 && (
                <>
                  {/* Bulk Find & Replace Section */}
                  <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-900 mb-3">Bulk Find & Replace</h3>
                    <p className="text-xs text-gray-600 mb-4">
                      Replace text in all visible link URLs. Enter the text to find and the replacement text. This will update all visible links that contain the search text.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Find:
                        </label>
                        <input
                          type="text"
                          value={bulkReplaceSearch}
                          onChange={(e) => setBulkReplaceSearch(e.target.value)}
                          placeholder="Text to find..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          Replace with:
                        </label>
                        <input
                          type="text"
                          value={bulkReplaceWith}
                          onChange={(e) => setBulkReplaceWith(e.target.value)}
                          placeholder="Replacement text..."
                          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <button
                          onClick={handleBulkReplace}
                          disabled={!bulkReplaceSearch.trim()}
                          className="w-full px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          Apply Replace
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-gray-700">
                      {linksSearchTerm.trim() ? (
                        <>
                          Showing {sortedLinks.length} of {externalLinks.length} external link(s)
                          {linksSortColumn && (
                            <span className="text-gray-500 ml-2">
                              (sorted by {linksSortColumn === 'url' ? 'Link URL' : 'Note'} {linksSortDirection === 'asc' ? '↑' : '↓'})
                            </span>
                          )}
                        </>
                      ) : (
                        <>
                          Found {externalLinks.length} external link(s)
                          {linksSortColumn && (
                            <span className="text-gray-500 ml-2">
                              (sorted by {linksSortColumn === 'url' ? 'Link URL' : 'Note'} {linksSortDirection === 'asc' ? '↑' : '↓'})
                            </span>
                          )}
                        </>
                      )}
                    </p>
                    {linkEdits.size > 0 && (
                      <button
                        onClick={handleUpdateLinks}
                        disabled={updatingLinks}
                        className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                      >
                        {updatingLinks ? (
                          <>
                            <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Updating...
                          </>
                        ) : (
                          <>
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Save Changes ({linkEdits.size})
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-md">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th 
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLinksSort('url')}
                          >
                            <div className="flex items-center gap-1">
                              Link URL
                              {linksSortColumn === 'url' && (
                                <span className="text-gray-700">
                                  {linksSortDirection === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Link Text
                          </th>
                          <th 
                            className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleLinksSort('note')}
                          >
                            <div className="flex items-center gap-1">
                              Note
                              {linksSortColumn === 'note' && (
                                <span className="text-gray-700">
                                  {linksSortDirection === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Occurrences
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {sortedLinks.map((link, idx) => {
                          const editedUrl = linkEdits.get(link.url) ?? link.url;
                          const hasChanges = editedUrl !== link.url;
                          return (
                            <tr key={idx} className={`hover:bg-gray-50 ${hasChanges ? 'bg-yellow-50' : ''}`}>
                              <td className="px-3 py-2">
                                <input
                                  type="text"
                                  value={editedUrl}
                                  onChange={(e) => {
                                    const newEdits = new Map(linkEdits);
                                    if (e.target.value === link.url) {
                                      newEdits.delete(link.url);
                                    } else {
                                      newEdits.set(link.url, e.target.value);
                                    }
                                    setLinkEdits(newEdits);
                                  }}
                                  className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                                  placeholder="Enter new URL or leave empty to remove"
                                />
                                {hasChanges && (
                                  <div className="mt-1 flex items-center gap-2">
                                    <span className="text-xs text-gray-500 line-through">{link.url}</span>
                                    <span className="text-xs text-blue-600">→</span>
                                    <span className="text-xs text-green-600">{editedUrl || '(will be removed)'}</span>
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div className="text-xs text-gray-900 max-w-md truncate" title={link.linkText}>
                                  {link.linkText || '(no text)'}
                                </div>
                                {link.context && (
                                  <div className="text-xs text-gray-500 mt-1 max-w-md truncate" title={link.context}>
                                    {link.context}
                                  </div>
                                )}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                <Link
                                  to={`/note/${link.noteSlug}`}
                                  className="text-xs text-blue-600 hover:text-blue-800 hover:underline font-medium"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {link.noteTitle || link.noteName}
                                </Link>
                                <div className="text-xs text-gray-500">{link.noteSlug}</div>
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap text-xs text-gray-500">
                                {link.occurrences}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
                </>
              )}
            </>
          )}

          {/* Search/Replace Notes Tab - simulation (find only) by default */}
          {activeTab === 'search-replace' && (
            <>
              <p className="text-sm text-gray-600 mb-6">
                Find or replace text across all notes. Always run <strong>Find</strong> first to see which notes are affected (simulation). Use <strong>Replace in all notes</strong> only when you want to apply changes.
              </p>
              <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex flex-wrap items-end gap-3 mb-3">
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs text-gray-600 mb-1">Search for</label>
                    <input
                      type="text"
                      value={replaceSearchText}
                      onChange={(e) => setReplaceSearchText(e.target.value)}
                      placeholder="e.g. ΓÇó"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div className="flex-1 min-w-[140px]">
                    <label className="block text-xs text-gray-600 mb-1">Replace with</label>
                    <input
                      type="text"
                      value={replaceReplaceText}
                      onChange={(e) => setReplaceReplaceText(e.target.value)}
                      placeholder="e.g. •"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={handleReplaceFind}
                    disabled={replaceInProgress}
                    className="px-4 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {replaceInProgress ? 'Searching…' : 'Find in all notes'}
                  </button>
                </div>
                {replaceFindResult != null && (
                  <div className="mt-3 text-sm text-gray-700">
                    <p className="font-medium text-gray-800">
                      Found {replaceFindResult.totalCount} match(es) in {replaceFindResult.matches.length} note(s).
                    </p>
                    <ul className="mt-2 max-h-48 overflow-y-auto space-y-1 list-disc list-inside">
                      {replaceFindResult.matches.map((m, i) => (
                        <li key={i}>
                          <a
                            href={`/admin/settings?activeTab=notes-editor&editNote=${encodeURIComponent(m.noteName)}`}
                            target="editNote"
                            rel="opener"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {m.title || m.noteName}
                          </a>
                          : {m.count} match(es)
                        </li>
                      ))}
                    </ul>
                    <p className="mt-2 text-amber-700 text-xs">
                      To apply the replace, click &quot;Replace in all notes&quot; below. This will modify note content.
                    </p>
                  </div>
                )}
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <button
                    type="button"
                    onClick={handleReplaceApply}
                    disabled={replaceInProgress}
                    className="px-4 py-2 text-sm bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {replaceInProgress ? 'Replacing…' : 'Replace in all notes'}
                  </button>
                </div>
                {replaceApplyResult != null && (
                  <div className="mt-3 text-sm">
                    <p className="text-gray-700">Updated {replaceApplyResult.updated} note(s).</p>
                    {replaceApplyResult.errors.length > 0 && (
                      <div className="mt-1 text-amber-700">
                        {replaceApplyResult.errors.slice(0, 5).map((e, i) => <div key={i}>{e}</div>)}
                        {replaceApplyResult.errors.length > 5 && <div>…and {replaceApplyResult.errors.length - 5} more</div>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExternalImagesPage;
